import { Button, ButtonProps, HStack, Tooltip, VStack } from '@chakra-ui/react'
import {
  colors,
  FieldMap,
  FieldMapValue,
  FieldTypes,
  GenericFaxArgs,
  isConsentForm,
  PopulatedNode,
  SendFaxArgs,
  UpdateCallback,
} from '@hb/shared'

import arrayMutators from 'final-form-arrays'
import { useCallback, useContext, useMemo, useState } from 'react'
import { Form } from 'react-final-form'
import { FieldArray } from 'react-final-form-arrays'
import { ReactEditor } from 'slate-react'
import { AdditionalFilesField } from '../Assessments/AdditionalFilesField'
import { BlueButton } from '../Buttons'
import { FormElement } from '../forms/Input'
import { DefaultModal } from '../Modals/DefaultModal'
import { TextEditorContext } from '../RichText/context'

import { PopUpMessageContext, ThemeContext } from '@hb/react'
import { useProfile } from '../../contexts/user/ProfileContext'
import { CustomEditor } from '../../types/editor'
import { TemplateErrorsAlert } from './TemplateErrorsAlert'

export type OnFax = (args: GenericFaxArgs) => Promise<UpdateCallback>
export const useOnFax = (onFax: OnFax | undefined) => {
  // TODO: show a dialog to collect phone number to fax to
  const [textToSend, setTextToSend] = useState<string | null>(null)
  const [faxSending, setFaxSending] = useState(false)
  const [faxErrors, setFaxErrors] = useState<Array<PopulatedNode> | null>(null)
  const { processResponse } = useContext(PopUpMessageContext)
  const { assessmentId } = useProfile()

  const handleSubmitFaxNumber = useCallback(
    async ({
      faxToNumber,
      notes,
      fileIds,
    }: Omit<SendFaxArgs, 'assessmentId' | 'html' | 'category'>): Promise<UpdateCallback> => {
      if (!onFax) {
        return { error: 'Internal error - missing submit function' }
      }
      if (!textToSend) {
        return { error: 'internal error - missing text to send' }
      }
      setFaxSending(true)
      const res = await onFax({
        faxToNumber,
        notes,
        assessmentId,
        html: textToSend,
        fileIds,
      })
      if (res.success) {
        setTextToSend(null)
        setFaxErrors(null)
      }
      setFaxSending(false)
      return processResponse(res)
    },
    [textToSend, onFax, processResponse, assessmentId],
  )

  const handleFaxClick = useCallback((text: string) => {
    setTextToSend(text)
  }, [])

  return {
    faxSending,
    faxErrors,
    textToSend,
    handleFax: handleFaxClick,
    handleSubmitFaxNumber,
    onCancel: () => {
      setTextToSend(null)
      setFaxErrors(null)
    },
  }
}

const faxDetails: FieldMap = {
  name: 'Fax Details',
  children: {
    faxNumber: {
      type: FieldTypes.PHONE,
      placeholder: 'Number to fax to',
    },
    notes: {
      type: FieldTypes.TEXTAREA,
      optional: true,
      placeholder: 'Notes...',
    },
  },
} as const

export const FaxDetailsEntryModal = ({
  onSubmit,
  isOpen,
  onCancel,
}: {
  isOpen: boolean
  onSubmit: (
    args: Omit<SendFaxArgs, 'html' | 'assessmentId' | 'category'>,
  ) => Promise<UpdateCallback>
  onCancel: () => void
}) => {
  const submit = useCallback(
    (data: FieldMapValue) =>
      onSubmit({
        faxToNumber: data.faxNumber,
        notes: data.notes,
        fileIds: data.fileIds,
      }),
    [onSubmit],
  )
  return (
    <DefaultModal
      isOpen={isOpen}
      onClose={onCancel}
      closeOnOverlayClick={false}
      overlayHeader
      render={() => (
        <ThemeContext.Provider value={{ theme: 'detailed' }}>
          <Form onSubmit={submit} mutators={{ ...arrayMutators }}>
            {({ handleSubmit, submitting }) => (
              <VStack w="100%" py={3} px={5} align="flex-start" spacing={0}>
                <FormElement name="faxNumber" field={faxDetails.children.faxNumber} />
                <FormElement name="notes" field={faxDetails.children.notes} />
                <FieldArray name="fileIds">
                  {({ fields, meta }) => <AdditionalFilesField meta={meta} fields={fields} />}
                </FieldArray>
                <HStack w="100%" pt={2}>
                  <Button
                    onClick={handleSubmit}
                    isLoading={submitting}
                    ml="auto"
                    size="sm"
                    bg={colors.green.hex}
                    color="white">
                    Send Fax
                  </Button>
                </HStack>
              </VStack>
            )}
          </Form>
        </ThemeContext.Provider>
      )}
    />
  )
}

export const EditorFaxButton = ({
  onCancel,
  faxErrors,
  handleFax,
  faxSending,
  handleSubmitFaxNumber,
  textToSend,
  editor,
  ...props
}: ButtonProps &
  ReturnType<typeof useOnFax> & {
    editor?: CustomEditor
  }) => {
  const { mode, document } = useContext(TextEditorContext)
  // const isEnabled = useMemo(() => {
  //   if (mode !== 'View') return false
  //   if (faxSending) return false
  //   if (!document) return false
  //   if (isConsentForm(document)) return document.signedOn
  //   return true
  // }, [mode, document, faxSending])

  const disabledReason = useMemo(() => {
    if (mode !== 'View') return 'Must be in View mode to send fax'
    if (faxSending) return 'Fax is already sending'
    if (document && isConsentForm(document))
      return document.signedOn ? null : 'Consent form is not signed'
    return null
  }, [mode, document, faxSending])

  return (
    <>
      <Tooltip bg="red.600" placement="top" hasArrow label={disabledReason}>
        <BlueButton
          mt={0}
          size="xs"
          style={{ fontSize: '13px' }}
          isLoading={faxSending}
          onClick={() => {
            if (!disabledReason)
              handleFax(editor ? (ReactEditor.toDOMNode(editor, editor).innerHTML as any) : '')
          }}
          {...props}
          // pointerEvents = {isEnabled ? 'auto' : 'none'}
          opacity={disabledReason ? 0.5 : 1}>
          Send Fax
        </BlueButton>
      </Tooltip>
      <TemplateErrorsAlert errors={faxErrors} onCancel={onCancel} />
      <FaxDetailsEntryModal
        onCancel={onCancel}
        isOpen={!!textToSend}
        onSubmit={handleSubmitFaxNumber}
      />
    </>
  )
}
